import { useEffect, useRef } from 'react';
import type { PropsWithChildren, ReactElement } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import type { FieldValues, UseFormTrigger } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { getDialCodeFromCountryCode } from '@xing-com/crate-jobs-domain-apply-helpers';
import { isValidPhoneNumberForCountry } from '@xing-com/crate-jobs-helpers';

import { FormField } from './form-fields.common.styles';
import type { FormFieldProps } from './types';

const DEFAULT_PHONE_NUMBER_MAX_LENGTH = 15;

type PhoneFieldProps<T extends FieldValues> = FormFieldProps<T> & {
  countryCode?: string;
  required?: boolean;
  trigger?: UseFormTrigger<T>;
};

export const PhoneField = <T extends FieldValues>({
  control,
  path,
  onFocus,
  countryCode,
  required = false,
  trigger,
}: PropsWithChildren<PhoneFieldProps<T>>): ReactElement => {
  const intl = useIntl();
  const phoneNumber = useWatch({ control, name: path });
  const phoneNumberRef = useRef(phoneNumber);
  phoneNumberRef.current = phoneNumber;

  useEffect(() => {
    if (phoneNumberRef.current) {
      trigger?.(path);
    }
  }, [countryCode, path, trigger]);

  return (
    <Controller
      name={path}
      control={control}
      render={({ field, fieldState }) => (
        <FormField
          variant="plain"
          size="medium"
          label={intl.formatMessage({
            id: 'JOBS_INSTANT_APPLY_FORM_PHONE_NUMBER_LABEL',
          })}
          maxLength={DEFAULT_PHONE_NUMBER_MAX_LENGTH}
          {...field}
          error={!!fieldState.error}
          helperText={
            fieldState?.error
              ? intl.formatMessage({
                  id:
                    fieldState.error.type === 'required'
                      ? 'JOBS_INSTANT_APPLY_FORM_PHONE_FIELD_EMPTY_ERROR'
                      : 'JOBS_INSTANT_APPLY_FORM_PHONE_FIELD_INVALID_ERROR',
                })
              : undefined
          }
          onFocus={onFocus}
          onChange={(event) => {
            field.onChange(event);
            trigger?.(path);
          }}
          autoComplete="tel-local"
        />
      )}
      rules={{
        required,
        maxLength: DEFAULT_PHONE_NUMBER_MAX_LENGTH,
        validate: (value: string) =>
          !value ||
          isValidPhoneNumberForCountry(
            value,
            countryCode,
            countryCode ? getDialCodeFromCountryCode(countryCode) : undefined
          ),
      }}
    />
  );
};
